import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ArrowLeft, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatPage = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [matchData, setMatchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchChatData();
    const interval = setInterval(fetchMessages, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, [matchId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchChatData = async () => {
    try {
      const [matchesRes, messagesRes] = await Promise.all([
        axios.get(`${API_URL}/matches`),
        axios.get(`${API_URL}/messages/${matchId}`)
      ]);

      const match = matchesRes.data.find(m => m.match_id === matchId);
      if (match) {
        setMatchData(match);
      }
      setMessages(messagesRes.data);
    } catch (error) {
      console.error('Failed to fetch chat data:', error);
      toast.error('Failed to load chat');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API_URL}/messages/${matchId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      await axios.post(`${API_URL}/messages`, {
        match_id: matchId,
        content: newMessage.trim()
      });
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto h-[calc(100vh-12rem)]" data-testid="chat-page">
        {/* Chat Header */}
        <div className="flex items-center gap-4 mb-4 pb-4 border-b border-border">
          <Button 
            variant="ghost" 
            size="icon"
            className="rounded-full"
            onClick={() => navigate('/matches')}
            data-testid="back-to-matches-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          
          {matchData && (
            <div className="flex items-center gap-3">
              <Avatar className="w-10 h-10">
                <AvatarImage src={matchData.user.profile_photo_url} />
                <AvatarFallback className="bg-primary/10 text-primary">
                  {matchData.user.display_name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <h2 className="font-medium">{matchData.user.display_name}</h2>
                <p className="text-sm text-muted-foreground">
                  {Math.round(matchData.compatibility_score)}% Match
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Messages Container */}
        <Card className="rounded-2xl border-border/50 shadow-soft h-[calc(100%-8rem)] flex flex-col">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <div className="h-full flex items-center justify-center text-center">
                <div>
                  <p className="text-muted-foreground mb-2">No messages yet</p>
                  <p className="text-sm text-muted-foreground">
                    Start the conversation with {matchData?.user.display_name}!
                  </p>
                </div>
              </div>
            ) : (
              messages.map((message) => {
                const isMine = message.sender_id === user?.id;
                return (
                  <div 
                    key={message.id}
                    className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                    data-testid={`message-${message.id}`}
                  >
                    <div className={`max-w-[75%] ${isMine ? 'items-end' : 'items-start'}`}>
                      <div className={isMine ? 'message-sent' : 'message-received'}>
                        <p>{message.content}</p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 px-1">
                        {formatTime(message.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <form onSubmit={handleSend} className="p-4 border-t border-border">
            <div className="flex gap-2">
              <Input
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                className="rounded-full"
                disabled={sending}
                data-testid="message-input"
              />
              <Button 
                type="submit" 
                size="icon" 
                className="rounded-full shrink-0"
                disabled={!newMessage.trim() || sending}
                data-testid="send-message-btn"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </Layout>
  );
};

export default ChatPage;
